// Колоните на „Натовареност" — един ред = един отрязък от времето, както в
// „Продажби по периоди": колоната „Период" с етикет и подетикет, подредена по
// ключа на BE (не по текста — „понеделник" би застанал след „вторник").
// Броячите по статус са отделни колони, за да се подреждат („кой час има
// най-много неявили се") и да влизат в реда „Общо".

import { useMemo } from "react";
import { createColumnHelper, type Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import type { TextCellValue } from "../../../../components/dataTable";
import { formatReservationMinutes } from "./reservationReportFormat";
import type { OccupancyPeriodRow } from "./occupancyMath";

const helper = createColumnHelper<OccupancyPeriodRow>();

const MONO = "font-pos-mono text-[12.5px] font-semibold tabular-nums";

type MinutesPicker = (row: OccupancyPeriodRow) => number | null;

const minutesSorting =
  (pickMinutes: MinutesPicker) =>
  (rowA: Row<OccupancyPeriodRow>, rowB: Row<OccupancyPeriodRow>) =>
    (pickMinutes(rowA.original) ?? -1) - (pickMinutes(rowB.original) ?? -1);

const countColumn = (id: keyof OccupancyPeriodRow, header: string, size = 92) =>
  helper.accessor((row) => row[id] as number, {
    id,
    header,
    size,
    meta: {
      cell: { kind: "num" },
      total: "sum",
      hideBelow: "tablet",
      excel: { format: "number" },
    },
  });

export const useOccupancyColumns = () => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      helper.accessor(
        (row): TextCellValue => ({ primary: row.label, secondary: row.sublabel }),
        {
          id: "period",
          header: t("reportsPage.salesPeriodsPeriodColumn"),
          size: 190,
          sortingFn: (rowA, rowB) => rowA.original.key.localeCompare(rowB.original.key),
          meta: {
            cell: { kind: "text", lines: 2 },
            filter: { kind: "text" },
            alwaysVisible: true,
            mobileRole: "title",
            excel: { format: "text" },
          },
        },
      ),

      helper.accessor((row) => row.reservations, {
        id: "reservations",
        header: t("reportsPage.rvKpiReservations"),
        size: 110,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.guests, {
        id: "guests",
        header: t("reportsPage.rvKpiGuests"),
        size: 92,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      countColumn("upcoming", t("reportsPage.rvStatus_upcoming")),
      countColumn("seated", t("reportsPage.rvStatus_seated")),
      countColumn("completed", t("reportsPage.rvStatus_completed")),
      countColumn("cancelled", t("reportsPage.rvStatus_cancelled")),

      helper.accessor((row) => row.expired, {
        id: "expired",
        header: t("reportsPage.rvStatus_expired"),
        size: 104,
        cell: (context) => (
          <span
            className={`${MONO} ${context.getValue() > 0 ? "text-danger-ink" : "text-faint"}`}
          >
            {context.getValue()}
          </span>
        ),
        meta: {
          align: "right",
          total: "sum",
          totalCell: { kind: "num" },
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.avgPlannedMinutes, {
        id: "avg_planned_minutes",
        header: t("reportsPage.rvColumnAvgDuration"),
        size: 130,
        sortingFn: minutesSorting((row) => row.avgPlannedMinutes),
        cell: (context) => (
          <span className={`${MONO} text-ink`}>
            {formatReservationMinutes(t, context.getValue())}
          </span>
        ),
        meta: { align: "right", hideBelow: "tablet", excel: { format: "text" } },
      }),

      helper.accessor((row) => row.avgSeatedMinutes, {
        id: "avg_seated_minutes",
        header: t("reportsPage.rvColumnAvgStay"),
        size: 130,
        sortingFn: minutesSorting((row) => row.avgSeatedMinutes),
        cell: (context) => (
          <span
            className={`${MONO} ${context.getValue() === null ? "text-faint" : "text-ink"}`}
          >
            {formatReservationMinutes(t, context.getValue())}
          </span>
        ),
        meta: { align: "right", hideBelow: "tablet", excel: { format: "text" } },
      }),
    ],
    [t],
  );
};
